/* eslint-disable no-unused-vars */
import { useState, useEffect, useCallback, useRef } from "react";

function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void, () => void, boolean] {
  const initialValueRef = useRef(initialValue);

  useEffect(() => {
    initialValueRef.current = initialValue;
  }, [initialValue]);

  // Initialize state with initialValue to prevent hydration mismatch.
  // The actual value will be read from localStorage in the useEffect below.
  const [state, setState] = useState<T>(initialValue);
  const [hydratedKey, setHydratedKey] = useState<string | null>(null);

  // Read from localStorage when key changes or on mount (client-side)
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const item = window.localStorage.getItem(key);
        // If item exists, parse it. If not, use initialValue (e.g., empty array for new key)
        setState(item ? JSON.parse(item) : initialValueRef.current);
        setHydratedKey(key);
      } catch (error) {
        console.warn(`Error reading localStorage key "${key}":`, error);
        setState(initialValueRef.current);
        setHydratedKey(key);
      }
    }
  }, [key]);

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(state) : value;
        setState(valueToStore);
        if (typeof window !== "undefined") {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
          // Dispatch storage event so other hooks/listeners update
          window.dispatchEvent(
            new StorageEvent("storage", {
              key,
              newValue: JSON.stringify(valueToStore),
            })
          );
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, state]
  );

  const removeItem = useCallback(() => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(key);
        setState(initialValueRef.current);
        window.dispatchEvent(
          new StorageEvent("storage", {
            key,
            newValue: null,
          })
        );
      }
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  }, [key]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        try {
          // Use newValue if available, otherwise read from store (handle manual dispatch vs native)
          const rawValue = event.newValue || window.localStorage.getItem(key);
          const newValue = rawValue
            ? JSON.parse(rawValue)
            : initialValueRef.current;
          setState(newValue);
        } catch (error) {
          console.warn(
            `Error parsing storage event value for key "${key}":`,
            error
          );
        }
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("storage", handleStorageChange);
      return () => window.removeEventListener("storage", handleStorageChange);
    }
  }, [key]);

  return [state, setValue, removeItem, hydratedKey === key];
}

export default useLocalStorage;
